import { useState } from 'react'
import chromeDesktopSite from './assets/help/chrome-desktop-site.png'
import type { MicAccess } from './deviceProbe'
import { HelpDialog } from './HelpDialog'
import { t } from './i18n'
import {
  detectPlatform,
  type HelpDevice,
  type HelpPhoto,
  type HelpStep,
  helpSteps,
} from './micHelp'
import { micHelpTitleKey } from './micHelpTitle'

const HELP_PHOTOS: Record<HelpPhoto, string> = {
  'chrome-desktop-site': chromeDesktopSite,
}

/**
 * "Your microphone isn't working — here's how to turn it on."
 *
 * Shown to a CHILD (roughly 6-15, Arabic-first, RTL), so the rules are:
 *
 * - Never blame. "The browser is not allowing the microphone", never "you blocked
 *   it". They almost certainly tapped something once, weeks ago, by accident.
 * - The walkthrough auto-advances so a child who cannot yet read fluently still
 *   sees where to tap, and it loops — but it is pausable and never traps them.
 * - Retry is the biggest thing on screen. The whole point is that fixing the
 *   setting and getting back into the lesson is one tap, not a reload (a reload
 *   would lose the scrubbed join code and strand them on "No class link").
 * - Platform steps are progressive disclosure behind a confidence check, with
 *   "Not your browser?" always available. Confidently wrong steps are worse than
 *   generic ones — see micHelp.ts.
 *
 * Permission walkthroughs use a bundled screenshot of the real browser chrome.
 * Share/device frames stay as small SVG.
 */

interface Props {
  access: MicAccess
  /** Re-runs the probe. Resolves once the caller knows the new state. */
  onRetry: () => void | Promise<void>
  onDismiss: () => void
  retrying?: boolean
  /** Prejoin can be firmer than mid-lesson; mid-lesson must be escapable. */
  tone: 'prejoin' | 'inroom'
  device?: HelpDevice
}

export function MicHelpDialog({
  access,
  onRetry,
  onDismiss,
  retrying = false,
  tone,
  device = 'mic',
}: Props) {
  const guess = detectPlatform()
  // A low-confidence guess opens on the generic advice rather than a walkthrough
  // for chrome the child may not have.
  const [showGeneric, setShowGeneric] = useState(!guess.confident)
  const steps = helpSteps(showGeneric ? 'generic' : guess.platform, access, device)
  const title = t(micHelpTitleKey(device === 'camera' ? 'camera' : 'mic', access))

  // The standard lede says the browser is not allowing the microphone. For
  // `silent` that is false — it allowed it and handed us an empty track — and
  // telling someone to fix a permission that is already correct is how they stop
  // trusting the rest of the advice.
  const lede =
    device === 'camera'
      ? t('cam.help.lede')
      : access === 'silent'
        ? t('mic.help.ledeSilent')
        : t('mic.help.lede')

  return (
    <HelpDialog
      title={title}
      body={lede}
      steps={steps}
      onRetry={onRetry}
      retryLabel={t('mic.help.retry')}
      onDismiss={onDismiss}
      retrying={retrying}
      tone={tone}
      icon={device === 'camera' ? <CamOffIcon /> : <MicSlashIcon />}
      capabilityKind={access === 'blocked' ? (device === 'camera' ? 'cam' : 'mic') : undefined}
      platformToggle={
        !showGeneric && guess.confident
          ? {
              label: `${guess.label} · ${t('mic.help.notYourBrowser')}`,
              onToggle: () => setShowGeneric(true),
            }
          : undefined
      }
    />
  )
}

/** Permission walks use a real screenshot. Share/device frames stay as SVG. */
export function StepArt({ step }: { step: HelpStep | undefined }) {
  if (!step) return null
  if (step.photo) {
    return <img className="mic-help__photo" src={HELP_PHOTOS[step.photo]} alt="" />
  }
  const common = { className: 'mic-help__art', viewBox: '0 0 240 132', role: 'img' as const }

  switch (step.art) {
    case 'menu-dots':
    case 'address-bar':
    case 'padlock':
    case 'site-settings':
    case 'ios-settings':
    case 'mic-allow':
      return null
    // A device dropdown: a select showing the current input with a chevron, an
    // open list below, the chosen device lit.
    case 'device-pick':
      return (
        <svg {...common} aria-label="">
          <BrowserFrame />
          <rect
            x={56}
            y={40}
            width={128}
            height={18}
            rx={5}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.4}
            opacity={0.45}
          />
          <MicGlyph x={70} y={49} />
          <rect x={84} y={46} width={70} height={6} rx={3} fill="currentColor" opacity={0.5} />
          <Chevron x={172} y={49} down />
          <rect className="mic-help__sheet" x={56} y={62} width={128} height={48} rx={6} />
          <rect className="mic-help__highlight" x={62} y={66} width={116} height={18} rx={5} />
          <MicGlyph x={74} y={75} />
          <rect x={88} y={72} width={64} height={6} rx={3} fill="currentColor" opacity={0.75} />
          <rect x={74} y={94} width={80} height={6} rx={3} fill="currentColor" opacity={0.3} />
        </svg>
      )
    // Chrome's share picker with its three source chips: the Chrome/Browser Tab
    // chip (a mini tabbed-window glyph) is the lit choice, not Window or Screen.
    case 'picker-tab':
      return (
        <svg {...common} aria-label="">
          <BrowserFrame />
          <rect className="mic-help__sheet" x={30} y={26} width={180} height={90} rx={8} />
          <rect x={42} y={34} width={90} height={5} rx={2.5} fill="currentColor" opacity={0.4} />
          <rect className="mic-help__highlight" x={40} y={46} width={48} height={18} rx={5} />
          <TabGlyph x={48} y={50} />
          <rect x={66} y={53} width={16} height={4} rx={2} fill="currentColor" opacity={0.75} />
          <rect
            x={94}
            y={46}
            width={48}
            height={18}
            rx={5}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.3}
            opacity={0.3}
          />
          <rect
            x={148}
            y={46}
            width={48}
            height={18}
            rx={5}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.3}
            opacity={0.3}
          />
          <rect
            x={40}
            y={70}
            width={156}
            height={38}
            rx={5}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.3}
            opacity={0.3}
          />
          <rect x={48} y={78} width={90} height={4} rx={2} fill="currentColor" opacity={0.25} />
          <rect x={48} y={88} width={70} height={4} rx={2} fill="currentColor" opacity={0.25} />
        </svg>
      )
    // Same picker, now the star is the bottom "Also share tab audio" row: a
    // speaker glyph and a toggle sliding clearly ON. Tab chip shows a check.
    case 'picker-audio':
      return (
        <svg {...common} aria-label="">
          <BrowserFrame />
          <rect className="mic-help__sheet" x={30} y={26} width={180} height={90} rx={8} />
          <rect x={42} y={34} width={90} height={5} rx={2.5} fill="currentColor" opacity={0.4} />
          <rect
            x={40}
            y={46}
            width={48}
            height={18}
            rx={5}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.3}
            opacity={0.4}
          />
          <TabGlyph x={46} y={50} />
          <Check x={72} y={55} />
          <rect
            x={94}
            y={46}
            width={48}
            height={18}
            rx={5}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.3}
            opacity={0.25}
          />
          <rect
            x={148}
            y={46}
            width={48}
            height={18}
            rx={5}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.3}
            opacity={0.25}
          />
          <rect className="mic-help__highlight" x={40} y={74} width={156} height={24} rx={6} />
          <SpeakerGlyph x={56} y={86} />
          <rect x={72} y={83} width={72} height={6} rx={3} fill="currentColor" opacity={0.75} />
          <rect className="mic-help__toggle" x={166} y={79} width={28} height={14} rx={7} />
          <circle className="mic-help__knob" cx={187} cy={86} r={5} />
        </svg>
      )
    // macOS System Settings: sidebar + a list where the app row is lit with its
    // Screen-Recording/Mic toggle sliding ON.
    case 'mac-privacy':
      return (
        <svg {...common} aria-label="">
          <rect
            x={30}
            y={12}
            width={180}
            height={108}
            rx={10}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.6}
            opacity={0.45}
          />
          <rect x={30} y={12} width={52} height={108} rx={10} fill="currentColor" opacity={0.06} />
          <rect x={40} y={30} width={32} height={5} rx={2.5} fill="currentColor" opacity={0.3} />
          <rect x={40} y={44} width={32} height={5} rx={2.5} fill="currentColor" opacity={0.3} />
          <rect x={40} y={58} width={32} height={5} rx={2.5} fill="currentColor" opacity={0.3} />
          <rect x={98} y={24} width={72} height={6} rx={3} fill="currentColor" opacity={0.4} />
          <rect className="mic-help__highlight" x={94} y={46} width={104} height={22} rx={6} />
          <rect x={106} y={54} width={48} height={6} rx={3} fill="currentColor" opacity={0.75} />
          <rect className="mic-help__toggle" x={168} y={51} width={26} height={13} rx={6.5} />
          <circle className="mic-help__knob" cx={187} cy={57.5} r={4.5} />
          <rect x={106} y={80} width={60} height={6} rx={3} fill="currentColor" opacity={0.3} />
          <rect
            x={168}
            y={78}
            width={26}
            height={13}
            rx={6.5}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.3}
            opacity={0.35}
          />
        </svg>
      )
    // The lesson window pulled to the front over a dimmed one behind it, lit and
    // check-marked: "come back to this window."
    case 'focus-window':
      return (
        <svg {...common} aria-label="">
          <rect
            x={20}
            y={20}
            width={150}
            height={82}
            rx={8}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.4}
            opacity={0.25}
          />
          <rect className="mic-help__sheet" x={58} y={40} width={158} height={78} rx={8} />
          <rect className="mic-help__highlight" x={58} y={40} width={158} height={78} rx={8} />
          <rect x={68} y={48} width={70} height={5} rx={2.5} fill="currentColor" opacity={0.4} />
          <path
            d="m124 82 9 9 16-22"
            fill="none"
            stroke="currentColor"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    default:
      return null
  }
}

function BrowserFrame() {
  return (
    <>
      <rect
        x={8}
        y={8}
        width={224}
        height={116}
        rx={9}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        opacity={0.45}
      />
      <rect x={40} y={15} width={162} height={14} rx={7} fill="currentColor" opacity={0.12} />
    </>
  )
}

/** A microphone glyph centred on (x, y). */
function MicGlyph({ x, y }: { x: number; y: number }) {
  return (
    <g
      transform={`translate(${x} ${y})`}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
    >
      <rect x={-3} y={-8} width={6} height={11} rx={3} />
      <path d="M-5.5 -1a5.5 5.5 0 0 0 11 0" />
      <path d="M0 4.5v3" />
    </g>
  )
}

/** A speaker glyph centred on (x, y) — the "share tab audio" mark. */
function SpeakerGlyph({ x, y }: { x: number; y: number }) {
  return (
    <g
      transform={`translate(${x} ${y})`}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinejoin="round"
      strokeLinecap="round"
    >
      <path d="M-6 -2h3l4-3v10l-4-3h-3z" />
      <path d="M4 -3a5 5 0 0 1 0 8" />
    </g>
  )
}

/** A mini tabbed browser window — the "Chrome Tab / Browser Tab" chip glyph. */
function TabGlyph({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`} stroke="currentColor" strokeWidth={1.3} fill="none">
      <rect x={0} y={0} width={14} height={11} rx={1.6} />
      <path d="M0 4h14" />
      <rect
        x={1.5}
        y={1}
        width={5}
        height={2.4}
        rx={0.8}
        fill="currentColor"
        stroke="none"
        opacity={0.7}
      />
    </g>
  )
}

/** A row chevron. `down` rotates it for a dropdown affordance. */
function Chevron({ x, y, down = false }: { x: number; y: number; down?: boolean }) {
  return (
    <path
      d="m0 -3 3 3-3 3"
      transform={`translate(${x} ${y})${down ? ' rotate(90)' : ''}`}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity={0.5}
    />
  )
}

/** A small check centred on (x, y). */
function Check({ x, y }: { x: number; y: number }) {
  return (
    <path
      d="m-3 0 2.2 2.4L4 -3"
      transform={`translate(${x} ${y})`}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  )
}

function MicSlashIcon() {
  return (
    <svg viewBox="0 0 24 24" width={22} height={22} aria-hidden="true">
      <path
        d="M12 3a3 3 0 0 1 3 3v5a3 3 0 0 1-4.6 2.5M9 6a3 3 0 0 1 .2-1M5 11a7 7 0 0 0 10.6 6M19 11a7 7 0 0 1-.6 2.8M12 19v2M4 4l16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
      />
    </svg>
  )
}

function CamOffIcon() {
  return (
    <svg viewBox="0 0 24 24" width={22} height={22} aria-hidden="true">
      <path
        d="M4.3 3 3 4.3 5.7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 .9-.6L19.7 21 21 19.7 4.3 3ZM17 8.5V7a1 1 0 0 0-1-1H9.5l7.5 7.4V13l4 4V6l-4 2.5Z"
        fill="currentColor"
      />
    </svg>
  )
}
